import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  PersonOff,
  Person,
  FilterList,
} from '@mui/icons-material';
import DataTable, { Column } from '../common/DataTable';
import StaffForm from './StaffForm';
import StaffDetails from './StaffDetails';
import { Staff, staffService, CreateStaffData, UpdateStaffData, StaffFilters } from '../../services/staffService';
import { useAppDispatch } from '../../hooks/redux';
import { addNotification } from '../../store/slices/uiSlice';

const StaffList: React.FC = () => {
  const dispatch = useAppDispatch();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<StaffFilters>({});
  
  // Form states
  const [formOpen, setFormOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Details dialog states
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsStaff, setDetailsStaff] = useState<Staff | null>(null);
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuStaff, setMenuStaff] = useState<Staff | null>(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<Staff | null>(null);

  useEffect(() => {
    fetchStaff();
  }, [page, rowsPerPage, searchTerm, filters]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await staffService.getStaff({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        ...filters,
      });
      setStaff(response.staff);
      setTotalCount(response.pagination.total);
    } catch (error) {
      console.error('Error fetching staff:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to fetch staff members',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = () => {
    setSelectedStaff(null);
    setFormOpen(true);
  };

  const handleEditStaff = (staff: Staff) => {
    setSelectedStaff(staff);
    setFormOpen(true);
    setAnchorEl(null);
  };

  const handleViewStaff = (staff: Staff) => {
    setDetailsStaff(staff);
    setDetailsOpen(true);
    setAnchorEl(null);
  };

  const handleToggleStatus = async (staff: Staff) => {
    try {
      await staffService.toggleStaffStatus(staff.id);
      dispatch(addNotification({
        type: 'success',
        message: `Staff member ${staff.isActive ? 'deactivated' : 'activated'} successfully`,
      }));
      fetchStaff();
    } catch (error) {
      console.error('Error toggling staff status:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to update staff status',
      }));
    }
    setAnchorEl(null);
  };

  const handleDeleteStaff = (staff: Staff) => {
    setStaffToDelete(staff);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const confirmDeleteStaff = async () => {
    if (!staffToDelete) return;

    try {
      await staffService.deleteStaff(staffToDelete.id);
      dispatch(addNotification({
        type: 'success',
        message: 'Staff member deleted successfully',
      }));
      fetchStaff();
    } catch (error: any) {
      console.error('Error deleting staff:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to delete staff member',
      }));
    } finally {
      setDeleteDialogOpen(false);
      setStaffToDelete(null);
    }
  };

  const handleFormSubmit = async (data: CreateStaffData | UpdateStaffData) => {
    setFormLoading(true);
    try {
      if (selectedStaff) {
        await staffService.updateStaff(selectedStaff.id, data as UpdateStaffData);
        dispatch(addNotification({
          type: 'success',
          message: 'Staff member updated successfully',
        }));
      } else {
        await staffService.createStaff(data as CreateStaffData);
        dispatch(addNotification({
          type: 'success',
          message: 'Staff member added successfully',
        }));
      }
      setFormOpen(false);
      fetchStaff();
    } catch (error: any) {
      console.error('Error saving staff:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to save staff member',
      }));
    } finally {
      setFormLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, staff: Staff) => {
    setAnchorEl(event.currentTarget);
    setMenuStaff(staff);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuStaff(null);
  };

  const columns: Column[] = [
    {
      id: 'name',
      label: 'Staff Member',
      format: (_value: any, row: Staff) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: '#E8B4B8',
              width: 48,
              height: 48,
              fontWeight: 700,
            }}
          >
            {row.firstName[0]}{row.lastName[0]}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {row.firstName} {row.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.employeeId}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'email',
      label: 'Contact',
      format: (_value: any, row: Staff) => (
        <Box>
          <Typography variant="body2">{row.email}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.phone}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'department',
      label: 'Department',
      format: (value: string) => (
        <Chip
          label={value}
          size="small"
          sx={{
            bgcolor: 'rgba(232, 180, 184, 0.1)',
            color: '#E8B4B8',
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      id: 'position',
      label: 'Position',
    },
    {
      id: 'skills',
      label: 'Skills',
      format: (value: string[]) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.slice(0, 2).map((skill) => (
            <Chip 
              key={skill} 
              label={skill} 
              size="small" 
              variant="outlined"
              sx={{
                borderColor: 'rgba(232, 180, 184, 0.3)',
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            />
          ))}
          {value.length > 2 && (
            <Chip 
              label={`+${value.length - 2}`} 
              size="small" 
              variant="outlined"
              sx={{
                borderColor: 'rgba(232, 180, 184, 0.3)',
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            />
          )}
        </Box>
      ),
    },
    {
      id: 'user',
      label: 'Role',
      format: (_value: any, row: Staff) => (
        <Chip
          label={row.user.role.replace('_', ' ')}
          color={row.user.role === 'MANAGER' ? 'primary' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      id: 'isActive',
      label: 'Status',
      format: (value: boolean) => (
        <Chip
          label={value ? 'Active' : 'Inactive'}
          color={value ? 'success' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      format: (_value: any, row: Staff) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, row)}
          sx={{
            color: '#E8B4B8',
            '&:hover': {
              bgcolor: 'rgba(232, 180, 184, 0.1)',
            },
          }}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Staff Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddStaff}
          sx={{
            bgcolor: '#E8B4B8',
            fontWeight: 700,
            px: 3,
            py: 1.5,
            borderRadius: 3,
            '&:hover': {
              bgcolor: '#D4A4A8',
            },
          }}
        >
          Add Staff Member
        </Button>
      </Box>

      {/* Filters */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          bgcolor: 'rgba(232, 180, 184, 0.05)',
          borderRadius: 3,
          border: '1px solid rgba(232, 180, 184, 0.1)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="Search staff members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#E8B4B8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover fieldset': { borderColor: '#E8B4B8' },
                  '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={filters.department || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value || undefined }))}
                label="Department"
                sx={{
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                }}
              >
                <MenuItem value="">All Departments</MenuItem>
                <MenuItem value="Hair Styling">Hair Styling</MenuItem>
                <MenuItem value="Makeup">Makeup</MenuItem>
                <MenuItem value="Skincare">Skincare</MenuItem>
                <MenuItem value="Nail Care">Nail Care</MenuItem>
                <MenuItem value="Reception">Reception</MenuItem>
                <MenuItem value="Management">Management</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.isActive !== undefined ? filters.isActive.toString() : ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  isActive: e.target.value === '' ? undefined : e.target.value === 'true' 
                }))}
                label="Status"
                sx={{
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setFilters({})}
              sx={{
                borderColor: '#E8B4B8',
                color: '#E8B4B8',
                fontWeight: 600,
                borderRadius: 2,
                height: 56,
                '&:hover': {
                  borderColor: '#D4A4A8',
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                },
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Staff Table */}
      <Card 
        sx={{ 
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(232, 180, 184, 0.2)',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <DataTable
            columns={columns}
            rows={staff}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            loading={loading}
          />
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
          },
        }}
      >
        <MenuItem onClick={() => handleViewStaff(menuStaff!)}>
          <Visibility sx={{ mr: 2, color: '#06B6D4' }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleEditStaff(menuStaff!)}>
          <Edit sx={{ mr: 2, color: '#F59E0B' }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleToggleStatus(menuStaff!)}>
          {menuStaff?.isActive ? (
            <>
              <PersonOff sx={{ mr: 2, color: '#EF4444' }} />
              Deactivate
            </>
          ) : (
            <>
              <Person sx={{ mr: 2, color: '#10B981' }} />
              Activate
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleDeleteStaff(menuStaff!)}>
          <Delete sx={{ mr: 2, color: '#EF4444' }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Staff Form Dialog */}
      <StaffForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        staff={selectedStaff}
        loading={formLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#EF4444' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The staff member will be permanently removed from the system.
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{staffToDelete?.firstName} {staffToDelete?.lastName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteStaff}
            variant="contained"
            color="error"
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 700,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staff Details Dialog */}
      <StaffDetails
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        staff={detailsStaff}
        onEdit={(staff) => {
          setSelectedStaff(staff);
          setFormOpen(true);
          setDetailsOpen(false);
        }}
      />
    </Box>
  );
};

export default StaffList;